import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';

import { MapsAPILoader } from '@agm/core';

import { ActivatedRoute, Params, Router } from '@angular/router';
import { Dropdown, MessageService } from 'primeng';
import { ISupplierInboxJob } from '../../../interfaces/inbox-job';
import { ISupplier } from '../../../interfaces/supplier';
import {
  EventService,
  InboxJobsService,
  MapService,
  OrganisationsService,
  SupplierJobStoreService,
  UserService,
} from '../../../services';
import { SupplierInboxSubType } from '../../../enums/supplier-inbox-sub-type.enum';
import { FormControl, FormGroup } from '@angular/forms';
import { JobType } from '../../../components/vme-jobs/vme-jobs.component';
import moment from 'moment';
import { customCalendarCellClass } from '../../../utils/calendar-utils';
import { flatten } from '../../../utils/flatten';
import { ISupplierSearchInboxFilter } from '../../../interfaces/supplier-search-inbox-job-filter';
import { IMarkerPoint } from '../../../components/map-control/marker.point';
import { ControlsOfFlat } from '../../../utils/controls-of';
import { SupplierJobChannel } from '../../../enums/supplier-job-channel';
import { SupplierInboxType } from '../../../enums/supplier-inbox.type.enum';
import { Subscription } from 'rxjs-compat';
import { first } from 'rxjs/operators';
import { TaskSupplierScheduleSource } from '../../../components/task-supplier-schedule/task-supplier-schedule-store';
import { SelectItem } from 'primeng/api';
import { InputHelper } from '../../../utils/input.helper';
import { Message } from '../../../classes/Message';
import { JobAnalyticsService } from '../../../services/product-analytics/job-analytics.service';

interface InboxTabOption {
  label: string;
  value: SupplierInboxType;
  routerLink: any[];
  defaultSort?: number;
}

enum SortColumn {
  SupplierBuyPrice = 'SupplierBuyPrice',
  CreatedOn = 'CreatedOn',
  PickupEarliest = 'PickupEarliest',
  DistanceToPickup = 'DistanceToPickup',
  DistanceToDropOff = 'DistanceToDropOff',
}

enum SortValue {
  Default = null,
  HighestPrice = 2,
  LowestPrice = 3,
  OldestJob = 4,
  NewestJob = 5,
  StartingSoon = 6,
  StartingLater = 7,
  DistanceToPickup = 8,
  DistanceToDropOff = 9,
}

interface SortOption {
  label: string;
  value: number;
  ascending: boolean;
  sortBy: SortColumn;
}

enum DistanceFromOptionValue {
  CustomLocation = 1,
  Home = 2,
  CurrentLocation = 3,
}

interface DistanceFromOption {
  label: string;
  value: DistanceFromOptionValue;
  custom?: boolean;
  icon?: string;
}

enum PickupOrDropoff {
  Pickup = 1,
  Dropoff = 2,
}

interface FilterForm {
  pickupMaxDistance: number;
  dropoffMaxDistance: number;
  pickupDistanceFrom: DistanceFromOptionValue;
  dropoffDistanceFrom: DistanceFromOptionValue;
  pickupPostcode: string;
  dropoffPostcode: string;
  dateOfJob: any;
  totalDistance: number;
  movementType: number;
  excludeElectric: boolean;
  sortBy: number;
  regNo: string;
  fromLocation: IMarkerPoint;
  toLocation: IMarkerPoint;
  fromLocationName: string;
  toLocationName: string;
}

@Component({
  selector: 'supplier-jobs-inbox',
  templateUrl: './supplier-jobs-inbox.component.html',
  styleUrls: ['./supplier-jobs-inbox.component.scss', './inbox-tabs.scss'],
})
export class SupplierJobsInboxComponent implements OnInit, AfterViewInit, OnDestroy {
  protected readonly customCalendarCellClass = customCalendarCellClass;

  searchParameters: {} = {};

  PickupOrDropoff = PickupOrDropoff;

  SupplierInboxType = SupplierInboxType;

  private subscriptions: Subscription[] = [];

  private mapsApiLoaded = false;
  private jobSearchDurationStartedAt: number = Date.now();
  private jobSearchExitTracked: boolean = false;

  private jobCounts: { [key: number]: number } = {};

  loadingCounts = false;
  private currentLocation: { latitude: number; longitude: number; timestamp: string; name: string };

  private pickupPlacesAutocomplete: ElementRef;

  @ViewChild('pickupPlacesAutocomplete') set pickupContent(content: ElementRef) {
    this.pickupPlacesAutocomplete = content;
    if (this.pickupPlacesAutocomplete) {
      void this.initPlacesAutocomplete(this.pickupPlacesAutocomplete, PickupOrDropoff.Pickup);
    }
  }

  private dropoffPlacesAutocomplete: ElementRef;

  @ViewChild('dropoffPlacesAutocomplete') set dropoffContent(content: ElementRef) {
    this.dropoffPlacesAutocomplete = content;
    if (this.dropoffPlacesAutocomplete) {
      void this.initPlacesAutocomplete(this.dropoffPlacesAutocomplete, PickupOrDropoff.Dropoff);
    }
  }

  @ViewChild('dropoffDropDown') dropoffDropDown: Dropdown;

  @ViewChild('pickupDropDown') pickupDropDown: Dropdown;

  @HostListener('window:scroll', ['$event'])
  onWindowScroll($event: Event) {
    const number =
      window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    this.hideScrollUpCtrl = number > 500 ? false : true;
  }

  public isLoaded: boolean = false;

  public selectedJob: { job: ISupplierInboxJob };
  public inboxSubType: SupplierInboxSubType;

  private _selectedTab: SupplierInboxType;

  public hideScrollUpCtrl: boolean = true;

  scrollToTop = () => window.scroll(0, 0);

  get selectedTab(): SupplierInboxType {
    return this._selectedTab;
  }

  set selectedTab(value: SupplierInboxType) {
    this.updateQueryParam('selectedTab', value.toString());
    this._selectedTab = value;
    this.supplierJobStoreService.selectedTab = value;
    this.setDefaultSort();
    void this.runSearch(true);
  }

  private setDefaultSort() {
    const inboxTabOption: InboxTabOption = this.inboxTabOptions.find(
      (x) => x.value == this.selectedTab,
    );
    const option: number = inboxTabOption?.defaultSort ?? this.DEFAULT_SORT_VALUE;
    this.setSort(option);
  }

  private setSort(sortBy: number) {
    this.selectedSortOption = this.sortByOptions.find((x) => x.value == sortBy);
    this.formControls.sortBy.setValue(sortBy);
    this.filterForm.patchValue({ sortBy: sortBy });
  }

  public jobSearchPageTitle: string = 'jobs_search';
  public myJobPageTitle: string = 'jobs_inbox';
  public currentOrg: ISupplier;
  private isIndividualSupplier = false;

  public supplierInboxTabs: InboxTabOption[] = [
    {
      label: 'Available',
      routerLink: ['/app/supplierinbox'],
      value: SupplierInboxType.InboxMatched,
    },
    {
      label: 'Watched',
      routerLink: ['/app/supplierinbox'],
      value: SupplierInboxType.InboxBidWatch,
    },
    {
      label: 'Hidden',
      routerLink: ['/app/supplierinbox'],
      value: SupplierInboxType.InboxRejected,
    },
    {
      label: 'Directed',
      routerLink: ['/app/supplierinbox'],
      value: SupplierInboxType.InboxDirect,
    },
    {
      label: 'Alerts',
      routerLink: ['/app/supplierinbox'],
      value: SupplierInboxType.InboxJobAlert,
    },
  ];

  public existingJobsTabs: InboxTabOption[] = [
    {
      label: 'Waiting',
      routerLink: ['/app/existingjobs'],
      value: SupplierInboxType.ExistingWaiting,
    },
    {
      label: 'Live Now',
      routerLink: ['/app/existingjobs'],
      value: SupplierInboxType.ExistingLive,
    },
    {
      label: 'Completed',
      routerLink: ['/app/existingjobs'],
      value: SupplierInboxType.ExistingCompleted,
      defaultSort: SortValue.StartingLater,
    },
    {
      label: 'Cancelled',
      routerLink: ['/app/existingjobs'],
      value: SupplierInboxType.ExistingCancelled,
      defaultSort: SortValue.StartingLater,
    },
    {
      label: 'Closed',
      routerLink: ['/app/existingjobs'],
      value: SupplierInboxType.ExistingClosed,
      defaultSort: SortValue.StartingLater,
    },
  ];

  inboxTabOptions: InboxTabOption[] = [];

  filteredSortOptions: SortOption[] = [];

  public isUserImpersonate = false;
  public impersonatedUserGUID: string;

  constructor(
    private route: ActivatedRoute,
    private inboxJobsService: InboxJobsService,
    private userService: UserService,
    private router: Router,
    private eventService: EventService,
    private mapsAPILoader: MapsAPILoader,
    private mapService: MapService,
    public supplierJobStoreService: SupplierJobStoreService,
    public organisationService: OrganisationsService,
    public inputHelper: InputHelper,
    private jobAnalyticsService: JobAnalyticsService,
  ) {
    this.eventService.JobRejectionEvent.subscribe((event) => {
      if (event.action == 'jobRejected') {
        this.jobCounts[SupplierInboxType.InboxRejected]++;
        this.jobCounts[SupplierInboxType.InboxMatched]--;
      }

      if (event.action == 'jobUnrejected') {
        this.jobCounts[SupplierInboxType.InboxRejected]--;
        this.jobCounts[SupplierInboxType.InboxMatched]++;
      }
    });

    this.eventService.JobWatchedEvent.subscribe((event) => {
      if (event.watched) {
        this.jobCounts[SupplierInboxType.InboxBidWatch]++;
      } else {
        this.jobCounts[SupplierInboxType.InboxBidWatch]--;
      }
    });

    this.jobCounts[SupplierInboxType.InboxMatched] = 0;
    this.jobCounts[SupplierInboxType.InboxRejected] = 0;
    this.jobCounts[SupplierInboxType.InboxBidWatch] = 0;

    this.route.data.subscribe((data: any) => {
      this.inboxSubType = data.inboxSubTypeId;
    });

    this.route.queryParams.subscribe((params: Params) => {
      let defaultTab =
        this.inboxSubType == SupplierInboxSubType.JobSearch
          ? SupplierInboxType.InboxMatched
          : SupplierInboxType.ExistingWaiting;

      this.inboxTabOptions =
        this.inboxSubType == SupplierInboxSubType.JobSearch
          ? this.supplierInboxTabs
          : this.existingJobsTabs;

      let isGridView = params['isGridView'] == 'true';
      this.supplierJobStoreService.isGridView = isGridView;

      if (this.inboxTabOptions.find((tab) => tab.value == SupplierInboxType.ExistingUnallocated)) {
        defaultTab = SupplierInboxType.ExistingUnallocated;
      }

      const queryTab = Number(params['selectedTab']);

      if (queryTab != this._selectedTab) {
        this._selectedTab = queryTab;
        this.searchParameters = { ...this.createFilterVal() };
      }

      // In theory a missing isGridView param could be ignored as it would just be interpreted
      // as false. However, for the active route tab to display correctly the route params must match exactly
      if (!this._selectedTab || !params['isGridView']) {
        this.router.navigate([], { queryParams: { selectedTab: defaultTab, isGridView } });
      }
    });

    let sub = this.supplierJobStoreService.selectedJob$.subscribe((job) => {
      this.selectedJob = { job };
    });

    this.subscriptions.push(sub);

    void this.setupCurrentLocation();
  }

  get formControls() {
    return this.filterForm.controls;
  }

  async setupCurrentLocation() {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(async (position) => {
      if (position) {
        this.currentLocation = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          timestamp: moment(position.timestamp).format('YYYY-MM-DD HH:mm:ss'),
          name: '',
        };

        await this.loadGoogleMapsApi();

        try {
          const geocodeResult = await this.mapService.geocodeLatLng({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          console.log('geocodeResult', geocodeResult);
          this.currentLocation.name = geocodeResult.results[0]?.formatted_address;
        } catch (error) {}

        this.pickupDistanceFromOptions.push({
          label: ` <strong>${this.currentLocation.name}</strong>`,
          value: 3,
          custom: false,
          icon: 'fa-map-marker',
        });
        this.dropoffDistanceFromOptions.push({
          label: ` <strong>${this.currentLocation.name}</strong>`,
          value: 3,
          custom: false,
          icon: 'fa-map-marker',
        });
      }
    });
  }

  async loadGoogleMapsApi() {
    if (this.mapsApiLoaded) {
      return;
    }
    await this.mapsAPILoader.load();
    this.mapsApiLoaded = true;
  }

  async ngOnInit() {
    this.resetJobSearchDuration();

    this.attachSortByFilterToForm();
    this.attachDistanceOptionsListener();
    this.loadQueryParamFilters();
    void this.runSearch(true);

    //get org info
    await this.loadOrgInfo();

    if (this.inboxSubType !== SupplierInboxSubType.JobSearch) {
      const hasMultiDrivers = await this.organisationService.getOrganisationHasMultipleDrivers();
      if (hasMultiDrivers && !this.isIndividualSupplier) {
        this.inboxTabOptions.unshift({
          label: 'Unallocated',
          routerLink: ['/app/existingjobs'],
          value: SupplierInboxType.ExistingUnallocated,
        });

        this.selectedTab = SupplierInboxType.ExistingUnallocated;
        this.searchParameters = { ...this.createFilterVal() };
      }
    }

    let sub = this.supplierJobStoreService.editScheduleDialogVisible$.subscribe((visible) => {
      this.showTravelScheduler = visible;
    });

    this.subscriptions.push(sub);
  }

  async loadOrgInfo() {
    try {
      this.currentOrg = await this.userService.getCurrentUserOrg();
      this.isIndividualSupplier = this.currentOrg.OrganisationSubTypeId == 1;

      if (this.currentOrg.postcode.length > 0) {
        this.pickupDistanceFromOptions.find((x) => x.value == 2).label =
          'Home: <strong>' + this.currentOrg.postcode + '</strong>';
        this.dropoffDistanceFromOptions.find((x) => x.value == 2).label =
          'Home: <strong>' + this.currentOrg.postcode + '</strong>';

        this.formControls.pickupDistanceFrom.setValue(DistanceFromOptionValue.Home);
        this.pickupDistanceFromChanged({ value: DistanceFromOptionValue.Home }, true);
      }

      this.userService.isUserImpersonating().then((res) => {
        var resultData: any;
        resultData = res;
        if (resultData != null) {
          this.isUserImpersonate = resultData.isImpersonating;
          this.impersonatedUserGUID = resultData.isImpersonating
            ? resultData.impersonatedUserGUID
            : resultData.originalUserId;
        }
      });
      this.fetchCount();
    } catch (err) {
      console.log(`Error retrieving current org info ${err}`);
    }
  }

  ngAfterViewInit() {
    // void this.runSearch(true);
  }

  attachSortByFilterToForm(): void {
    this.subscriptions.push(
      this.filterForm.controls.fromLocation.valueChanges.subscribe(() => {
        this.updateSortByOptions();
      }),
    );

    this.subscriptions.push(
      this.filterForm.controls.toLocation.valueChanges.subscribe(() => {
        this.updateSortByOptions();
      }),
    );

    this.updateSortByOptions();
  }

  attachDistanceOptionsListener() {
    this.subscriptions.push(
      this.filterForm.controls.pickupPostcode.valueChanges.subscribe(() => {
        this.updatePickupDistanceOptions();
      }),
    );

    this.subscriptions.push(
      this.filterForm.controls.dropoffPostcode.valueChanges.subscribe(() => {
        this.updateDropoffDistanceOptions();
      }),
    );
  }

  async initPlacesAutocomplete(input: ElementRef, pickupOrDropoff: PickupOrDropoff) {
    try {
      await this.loadGoogleMapsApi();

      const options = {
        // bounds: defaultBounds,
        componentRestrictions: { country: 'gb' },
        fields: ['geometry', 'name'],
        strictBounds: false,
      };

      const autocomplete = new google.maps.places.Autocomplete(input.nativeElement, options);

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        console.log('Selected place', place);
        this.saveCustomLocation(pickupOrDropoff, place);
      });
    } catch (error) {
      console.error('Error initializing place autocomplete:', error);
    }
  }

  createFilterVal(): ISupplierSearchInboxFilter {
    const maxDistanceValue = this.formControls.totalDistance.value;
    const sort = this.sortByOptions.find((x) => x.value == this.filterForm.value.sortBy);

    const filterVal = {
      first: 0,
      rows: 20,
      sortField: sort?.sortBy,
      sortOrder: sort?.ascending ? 1 : -1,
      filters: {
        inboxType: { value: this._selectedTab },
        movementType: { value: this.formControls.movementType.value },
        regNo: { value: this.formControls.regNo.value?.trim() },
        isRefresh: { value: null },
        excludeElectric: { value: this.formControls.excludeElectric.value },
        maxDistanceToPickup: { value: this.formControls.pickupMaxDistance.value },
        maxDistanceToDropoff: { value: this.formControls.dropoffMaxDistance.value },
        pickupPostcode: { value: this.formControls.pickupPostcode.value?.trim() },
        dropoffPostcode: { value: this.formControls.dropoffPostcode.value?.trim() },
        fromEarliestPickupDate: { value: null },
        toEarliestDropoffDate: { value: null },
        distanceRange: { value: maxDistanceValue ? [0, maxDistanceValue] : null },
        fromLocation: { value: this.formControls.fromLocation.value },
        toLocation: { value: this.formControls.toLocation.value },
      },
    };

    const dateRange = this.formControls.dateOfJob.value;

    if (dateRange != null) {
      filterVal.filters.fromEarliestPickupDate = {
        value: moment(dateRange[0]).format('YYYY-MM-DD'),
      };
      filterVal.filters.toEarliestDropoffDate = {
        value: moment(dateRange[1] || dateRange[0]).format('YYYY-MM-DD'),
      };
    }

    return filterVal;
  }

  gridOptions = [
    { value: false, icon: 'fa fa-columns' },
    { value: true, icon: 'fa fa-list' },
  ];

  allDistanceOptions: SelectItem[] = [
    { label: 'Exact Postcode', value: -1 }, // Hack to allow for exact location as 0 won't work
    { label: '5', value: 5 },
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '75', value: 75 },
    { label: '100', value: 100 },
    { label: '125', value: 125 },
    { label: '150', value: 150 },
    { label: '175', value: 175 },
    { label: '200', value: 200 },
    { label: '200+', value: 500 },
  ];

  pickupDistanceOptions: SelectItem[] = [];
  dropoffDistanceOptions: SelectItem[] = [];

  pickupDistanceFromOptions: DistanceFromOption[] = [
    { label: 'Custom Location', value: DistanceFromOptionValue.CustomLocation, custom: true },
    { label: 'Home: Postcode', value: DistanceFromOptionValue.Home, icon: 'fa-home' },
  ];

  dropoffDistanceFromOptions: DistanceFromOption[] = [
    { label: 'Custom Location', value: DistanceFromOptionValue.CustomLocation, custom: true },
    { label: 'Home: Postcode', value: DistanceFromOptionValue.Home, icon: 'fa-home' },
  ];

  totalDistanceOptions = [
    { label: 'Any Total Distance', value: null },
    { label: '5 Miles Max.', value: 5 },
    { label: '10 Miles Max.', value: 10 },
    { label: '25 Miles Max.', value: 25 },
    { label: '50 Miles Max.', value: 50 },
    { label: '100 Miles Max.', value: 100 },
    { label: '200 Miles Max.', value: 200 },
    { label: '200+ Miles.', value: 999 },
  ];

  movementTypeOptions = [
    { label: 'Any Movement Type', value: null },
    { label: 'Driven', value: JobType.Driven },
    { label: 'Transported', value: JobType.Transported },
  ];

  fuelTypeOptions = [
    { label: 'Any Fuel', value: null },
    { label: 'Exclude Electric', value: true },
  ];

  sortByOptions: SortOption[] = [
    { label: 'Sort', value: SortValue.Default, ascending: false, sortBy: null },
    {
      label: 'Highest Price',
      value: SortValue.HighestPrice,
      sortBy: SortColumn.SupplierBuyPrice,
      ascending: false,
    },
    {
      label: 'Lowest Price',
      value: SortValue.LowestPrice,
      sortBy: SortColumn.SupplierBuyPrice,
      ascending: true,
    },
    {
      label: 'Oldest Job',
      value: SortValue.OldestJob,
      sortBy: SortColumn.CreatedOn,
      ascending: true,
    },
    {
      label: 'Newest Job',
      value: SortValue.NewestJob,
      sortBy: SortColumn.CreatedOn,
      ascending: false,
    },
    {
      label: 'Starting Soon',
      value: SortValue.StartingSoon,
      sortBy: SortColumn.PickupEarliest,
      ascending: true,
    },
    {
      label: 'Starting Later',
      value: SortValue.StartingLater,
      sortBy: SortColumn.PickupEarliest,
      ascending: false,
    },
    {
      label: 'Distance To Pick-Up',
      value: SortValue.DistanceToPickup,
      sortBy: SortColumn.DistanceToPickup,
      ascending: true,
    },
    {
      label: 'Distance To Drop-Off',
      value: SortValue.DistanceToDropOff,
      sortBy: SortColumn.DistanceToDropOff,
      ascending: true,
    },
  ];

  private DEFAULT_SORT_VALUE: number = SortValue.StartingSoon;

  public selectedSortOption: SortOption = this.sortByOptions.find(
    (x) => x.value == this.DEFAULT_SORT_VALUE,
  );

  filterForm: FormGroup<ControlsOfFlat<FilterForm>> = new FormGroup({
    pickupMaxDistance: new FormControl<number>(null),
    dropoffMaxDistance: new FormControl<number>(null),
    pickupDistanceFrom: new FormControl<DistanceFromOptionValue>(null),
    dropoffDistanceFrom: new FormControl<DistanceFromOptionValue>(null),
    pickupPostcode: new FormControl<string>(null),
    dropoffPostcode: new FormControl<string>(null),
    dateOfJob: new FormControl(null),
    totalDistance: new FormControl<number>(null),
    movementType: new FormControl<number>(null),
    excludeElectric: new FormControl<boolean>(null),
    sortBy: new FormControl<number>(this.DEFAULT_SORT_VALUE),
    regNo: new FormControl<string>(null),
    fromLocation: new FormControl<IMarkerPoint>(null),
    toLocation: new FormControl<IMarkerPoint>(null),
    fromLocationName: new FormControl<string>(null),
    toLocationName: new FormControl<string>(null),
  });

  dataEntry = true;

  layoutViewChanged(value: any) {
    this.updateQueryParam('isGridView', value.option.value ? 'true' : 'false');
  }

  ngOnDestroy() {
    this.trackJobSearchExit();
    this.subscriptions.forEach((sub) => sub?.unsubscribe());
  }

  @HostListener('window:beforeunload')
  onWindowBeforeUnload(): void {
    this.trackJobSearchExit();
  }

  updateQueryParam(paramName: string, paramValue: string) {
    // Get the current route's queryParams
    let queryParams = { ...this.route.snapshot.queryParams };

    // Update or set the query parameter
    queryParams[paramName] = paramValue;

    // Use navigate to update the URL without reloading the page
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      // Preserve the existing path parts of the URL.
      queryParamsHandling: 'merge',
    });
  }

  availableJobsCount(x: number) {
    return this.jobCounts[x];
  }

  submitSearch() {
    void this.fetchCount();
    void this.runSearch(false, true);
  }

  async runSearch(reset: boolean = false, track: boolean = false) {
    if (!reset) {
      this.setQueryParamFilters();
    }

    const filters = this.createFilterVal();

    if (track) {
      const pageStayDurationSeconds = this.getJobSearchDurationSeconds();
      this.jobAnalyticsService.sendJobSearchEvent(filters, pageStayDurationSeconds);
      this.resetJobSearchDuration();
    }

    this.searchParameters = { ...filters };
  }

  private getJobSearchDurationSeconds(): number {
    return Math.max(0, Math.round((Date.now() - this.jobSearchDurationStartedAt) / 1000));
  }

  private resetJobSearchDuration(): void {
    this.jobSearchDurationStartedAt = Date.now();
    this.jobSearchExitTracked = false;
  }

  private trackJobSearchExit(): void {
    if (this.jobSearchExitTracked) {
      return;
    }

    const filters = this.createFilterVal();
    this.jobAnalyticsService.sendJobSearchEvent(filters, this.getJobSearchDurationSeconds(), true);
    this.jobSearchExitTracked = true;
  }

  get supplierJobChannel(): SupplierJobChannel | null {
    switch (this._selectedTab) {
      case SupplierInboxType.InboxMatched:
        return this.supplierJobStoreService.isGridView
          ? SupplierJobChannel.GV_AllAvailable
          : SupplierJobChannel.AllAvailable;
      case SupplierInboxType.InboxBidWatch:
        return this.supplierJobStoreService.isGridView
          ? SupplierJobChannel.GV_Watched
          : SupplierJobChannel.Watched;
      case SupplierInboxType.InboxDirect:
        return this.supplierJobStoreService.isGridView
          ? SupplierJobChannel.GV_Directed
          : SupplierJobChannel.Directed;
      case SupplierInboxType.InboxRejected:
        return this.supplierJobStoreService.isGridView
          ? SupplierJobChannel.GV_Rejected
          : SupplierJobChannel.Rejected;
      case SupplierInboxType.InboxJobAlert:
        return this.supplierJobStoreService.isGridView
          ? SupplierJobChannel.GV_JobAlerts
          : SupplierJobChannel.JobAlerts;
    }
  }

  pickupDistanceFromChanged(event: { value: DistanceFromOptionValue }, dontTrack: boolean = false) {
    this.filterForm.patchValue({
      pickupPostcode: null,
    });

    // Home postcode
    if (event && event.value == DistanceFromOptionValue.Home) {
      this.filterForm.patchValue({ pickupPostcode: this.currentOrg.postcode });
      this.filterForm.patchValue({
        fromLocation: { latitude: this.currentOrg.latitude, longitude: this.currentOrg.longitude },
      });
    }

    if (event && event.value == DistanceFromOptionValue.CurrentLocation) {
      this.filterForm.patchValue({
        fromLocation: this.currentLocation,
      });
    }

    if (!dontTrack) {
      this.jobAnalyticsService.sendJobFilterApplyEvent('pickupLocation', event.value?.toString());
    }
  }

  onPickupDistanceChange(value) {
    this.jobAnalyticsService.sendJobFilterApplyEvent('pickupDistance', value?.toString());
  }

  dropoffDistanceFromChanged(event: any) {
    this.filterForm.patchValue({
      dropoffPostcode: null,
    });

    // Home postcode
    if (event && event.value == DistanceFromOptionValue.Home) {
      this.filterForm.patchValue({ dropoffPostcode: this.currentOrg.postcode });
      this.filterForm.patchValue({
        toLocation: { latitude: this.currentOrg.latitude, longitude: this.currentOrg.longitude },
      });
    }

    if (event && event.value == DistanceFromOptionValue.CurrentLocation) {
      this.filterForm.patchValue({
        toLocation: this.currentLocation,
      });
    }
    this.jobAnalyticsService.sendJobFilterApplyEvent('dropoffLocation', event.value?.toString());
  }

  onDropoffDistanceChange(value) {
    this.jobAnalyticsService.sendJobFilterApplyEvent('dropoffDistance', value?.toString());
  }

  onCalendarDateChange() {
    this.jobAnalyticsService.sendJobFilterApplyEvent('calendar', this.filterForm?.value?.dateOfJob);
  }

  onTotalDistanceChange($event) {
    this.jobAnalyticsService.sendJobFilterApplyEvent('totalDistance', $event.value);
  }

  onFuelTypeChange($event) {
    this.jobAnalyticsService.sendJobFilterApplyEvent('fuelType', $event.value);
  }

  onMovementTypeChange($event) {
    this.jobAnalyticsService.sendJobFilterApplyEvent('movementType', $event.value);
  }

  resetForm() {
    this.filterForm.reset();
    this.setDefaultSort();
    this.submitSearch();
  }

  resetDateOfJob() {
    this.filterForm.patchValue({ dateOfJob: null });
  }

  switchToDataEntry(e) {
    e.stopPropagation();
    this.dataEntry = true;
  }

  saveCustomLocation(type: PickupOrDropoff, location: google.maps.places.PlaceResult) {
    if (type == PickupOrDropoff.Pickup) {
      this.filterForm.patchValue({
        pickupPostcode: null,
      });

      this.filterForm.controls.pickupDistanceFrom.setValue(1);
      this.filterForm.controls.fromLocationName.setValue(location.name);

      this.filterForm.controls.fromLocation.setValue({
        latitude: location.geometry.location.lat(),
        longitude: location.geometry.location.lng(),
      });

      if (this.isPostcode(location.name)) {
        this.filterForm.controls.pickupPostcode.setValue(location.name);
      }

      this.pickupDropDown.hide(true);
      return;
    }

    this.filterForm.patchValue({
      dropoffPostcode: null,
    });

    this.filterForm.controls.dropoffDistanceFrom.setValue(1);
    this.filterForm.controls.toLocationName.setValue(location.name);

    this.filterForm.controls.toLocation.setValue({
      latitude: location.geometry.location.lat(),
      longitude: location.geometry.location.lng(),
    });

    if (this.isPostcode(location.name)) {
      this.filterForm.controls.dropoffPostcode.setValue(location.name);
    }

    this.dropoffDropDown.hide(true);
    // this.dataEntry = false;
  }

  inAvailableJobsTab(selectedTab: number) {
    return (
      selectedTab == SupplierInboxType.InboxMatched ||
      selectedTab == SupplierInboxType.InboxBidWatch ||
      selectedTab == SupplierInboxType.InboxRejected
    );
  }

  async fetchCount() {
    if (this.loadingCounts) {
      return Promise.resolve(true);
    }

    this.loadingCounts = true;

    const fetchParameters = { ...this.createFilterVal() };

    return this.inboxJobsService
      .getHiddenWatchingCounts(this.currentOrg.id, flatten(fetchParameters))
      .then((x) => {
        this.loadingCounts = false;
        this.jobCounts[SupplierInboxType.InboxMatched] = x['unrejectedJobCount'];
        this.jobCounts[SupplierInboxType.InboxRejected] = x['rejectedJobCount'];
        this.jobCounts[SupplierInboxType.InboxBidWatch] = x['watchedJobCount'];
      })
      .catch((e) => {
        this.loadingCounts = false;
      });
  }

  optionLabel(option: DistanceFromOption, pickupDropoff: PickupOrDropoff) {
    if (!option.custom) {
      return option.label;
    }

    if (pickupDropoff == PickupOrDropoff.Pickup) {
      return this.filterForm.controls.fromLocationName.value;
    } else {
      return this.filterForm.controls.toLocationName.value;
    }
  }

  isPostcode(inputVal: string) {
    return inputVal.match(/^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}|GIR ?0A{2})$/) != null;
  }

  updatePickupDistanceOptions() {
    const postcodeInputVal = (this.filterForm.controls.pickupPostcode.value ?? '').replace(' ', '');
    const isPostcode = this.isPostcode(postcodeInputVal);

    if (isPostcode) {
      this.pickupDistanceOptions = this.allDistanceOptions;
      return;
    }

    this.pickupDistanceOptions = this.allDistanceOptions.filter((x) => x.value != -1);
  }

  updateDropoffDistanceOptions() {
    const postcodeInputVal = (this.filterForm.controls.dropoffPostcode.value ?? '').replace(
      ' ',
      '',
    );
    const isPostcode = this.isPostcode(postcodeInputVal);

    if (isPostcode) {
      this.dropoffDistanceOptions = this.allDistanceOptions;
      return;
    }

    this.dropoffDistanceOptions = this.allDistanceOptions.filter((x) => x.value != -1);
  }

  updateSortByOptions() {
    let options = [...this.sortByOptions];

    if (!this.filterForm.controls.fromLocation.value) {
      options = options.filter((x) => x.value != 8);
    }

    if (!this.filterForm.controls.toLocation.value) {
      options = options.filter((x) => x.value != 9);
    }

    this.filteredSortOptions = options;
  }

  isMyJobsTab(selectedTab: number) {
    return this.existingJobsTabs.some((x) => x.value == selectedTab);
  }

  setQueryParamFilters() {
    const queryParams: any = {};
    const filterFormValue = this.filterForm.value;

    if (filterFormValue !== undefined) {
      queryParams.pickupMaxDistance =
        filterFormValue.pickupMaxDistance !== null ? filterFormValue.pickupMaxDistance : null;
      queryParams.dropoffMaxDistance =
        filterFormValue.dropoffMaxDistance !== null ? filterFormValue.dropoffMaxDistance : null;
      queryParams.pickupDistanceFrom =
        filterFormValue.pickupDistanceFrom !== null ? filterFormValue.pickupDistanceFrom : null;
      queryParams.dropoffDistanceFrom =
        filterFormValue.dropoffDistanceFrom !== null ? filterFormValue.dropoffDistanceFrom : null;
      queryParams.pickupPostcode = filterFormValue.pickupPostcode || null;
      queryParams.dropoffPostcode = filterFormValue.dropoffPostcode || null;
      queryParams.totalDistance =
        filterFormValue.totalDistance !== null ? filterFormValue.totalDistance : null;
      queryParams.movementType =
        filterFormValue.movementType !== null ? filterFormValue.movementType : null;
      queryParams.excludeElectric = filterFormValue.excludeElectric || null;
      queryParams.sortBy = filterFormValue.sortBy !== null ? filterFormValue.sortBy : null;
      queryParams.regNo = filterFormValue.regNo || null;

      if (filterFormValue.pickupDistanceFrom === DistanceFromOptionValue.CurrentLocation) {
        queryParams.fromLocationName = this.currentLocation.name;
      } else {
        queryParams.fromLocationName = filterFormValue.fromLocationName || null;
      }

      if (filterFormValue.dropoffDistanceFrom === DistanceFromOptionValue.CurrentLocation) {
        queryParams.toLocationName = this.currentLocation.name;
      } else {
        queryParams.toLocationName = filterFormValue.toLocationName || null;
      }

      if (filterFormValue.fromLocation !== null) {
        queryParams.fromLocationLatitude = filterFormValue.fromLocation.latitude;
        queryParams.fromLocationLongitude = filterFormValue.fromLocation.longitude;
        queryParams.fromLocationAddress = filterFormValue.fromLocation.address || null;
        queryParams.fromLocationRadius = filterFormValue.fromLocation.radius || null;
        queryParams.fromLocationColor = filterFormValue.fromLocation.color || null;
      } else {
        queryParams.fromLocationLatitude = null;
        queryParams.fromLocationLongitude = null;
        queryParams.fromLocationAddress = null;
        queryParams.fromLocationRadius = null;
        queryParams.fromLocationColor = null;
      }

      if (filterFormValue.toLocation !== null) {
        queryParams.toLocationLatitude = filterFormValue.toLocation.latitude;
        queryParams.toLocationLongitude = filterFormValue.toLocation.longitude;
        queryParams.toLocationAddress = filterFormValue.toLocation.address || null;
        queryParams.toLocationRadius = filterFormValue.toLocation.radius || null;
        queryParams.toLocationColor = filterFormValue.toLocation.color || null;
      } else {
        queryParams.toLocationLatitude = null;
        queryParams.toLocationLongitude = null;
        queryParams.toLocationAddress = null;
        queryParams.toLocationRadius = null;
        queryParams.toLocationColor = null;
      }

      const dateRange = filterFormValue.dateOfJob;
      if (dateRange != null) {
        queryParams.fromEarliestPickupDate = moment(dateRange[0]).format('YYYY-MM-DD');
        queryParams.toLatestDropoffDate = moment(dateRange[1] || dateRange[0]).format('YYYY-MM-DD');
      } else {
        queryParams.fromEarliestPickupDate = null;
        queryParams.toLatestDropoffDate = null;
      }
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      queryParamsHandling: 'merge',
    });
  }

  loadQueryParamFilters() {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      if (params['pickupMaxDistance'] !== undefined) {
        this.filterForm.patchValue({ pickupMaxDistance: +params['pickupMaxDistance'] });
      }

      if (params['dropoffMaxDistance'] !== undefined) {
        this.filterForm.patchValue({ dropoffMaxDistance: +params['dropoffMaxDistance'] });
      }

      if (params['pickupDistanceFrom'] !== undefined) {
        let pickupDistanceFromValue = +params['pickupDistanceFrom'];

        if (pickupDistanceFromValue === DistanceFromOptionValue.CurrentLocation) {
          pickupDistanceFromValue = DistanceFromOptionValue.CustomLocation;
        }

        this.filterForm.patchValue({ pickupDistanceFrom: pickupDistanceFromValue });

        if (
          pickupDistanceFromValue === DistanceFromOptionValue.CustomLocation &&
          params['fromLocationName'] !== undefined
        ) {
          this.filterForm.patchValue({ fromLocationName: params['fromLocationName'] });
        }
      }

      if (params['dropoffDistanceFrom'] !== undefined) {
        let dropoffDistanceFromValue = +params['dropoffDistanceFrom'];

        if (dropoffDistanceFromValue === DistanceFromOptionValue.CurrentLocation) {
          dropoffDistanceFromValue = DistanceFromOptionValue.CustomLocation;
        }

        this.filterForm.patchValue({ dropoffDistanceFrom: dropoffDistanceFromValue });

        if (
          dropoffDistanceFromValue === DistanceFromOptionValue.CustomLocation &&
          params['toLocationName'] !== undefined
        ) {
          this.filterForm.patchValue({ toLocationName: params['toLocationName'] });
        }
      }

      if (params['pickupPostcode'] !== undefined) {
        this.filterForm.patchValue({ pickupPostcode: params['pickupPostcode'] });
      }

      if (params['dropoffPostcode'] !== undefined) {
        this.filterForm.patchValue({ dropoffPostcode: params['dropoffPostcode'] });
      }

      if (params['totalDistance'] !== undefined) {
        this.filterForm.patchValue({ totalDistance: +params['totalDistance'] });
      }

      if (params['movementType'] !== undefined) {
        this.filterForm.patchValue({ movementType: +params['movementType'] });
      }

      if (params['excludeElectric'] !== undefined) {
        this.filterForm.patchValue({
          excludeElectric: params['excludeElectric'] === 'true',
        });
      }

      if (params['sortBy'] !== undefined) {
        this.setSort(+params['sortBy']);
      } else {
        this.setDefaultSort();
      }

      if (params['regNo'] !== undefined) {
        this.filterForm.patchValue({ regNo: params['regNo'] });
      }

      if (
        params['fromLocationLatitude'] !== undefined &&
        params['fromLocationLongitude'] !== undefined
      ) {
        this.filterForm.patchValue({
          fromLocation: {
            latitude: +params['fromLocationLatitude'],
            longitude: +params['fromLocationLongitude'],
            address: params['fromLocationAddress'],
            radius: params['fromLocationRadius'] ? +params['fromLocationRadius'] : undefined,
            color: params['fromLocationColor'],
          },
        });
      }

      if (
        params['toLocationLatitude'] !== undefined &&
        params['toLocationLongitude'] !== undefined
      ) {
        this.filterForm.patchValue({
          toLocation: {
            latitude: +params['toLocationLatitude'],
            longitude: +params['toLocationLongitude'],
            address: params['toLocationAddress'],
            radius: params['toLocationRadius'] ? +params['toLocationRadius'] : undefined,
            color: params['toLocationColor'],
          },
        });
      }

      if (params['fromLocationName'] !== undefined) {
        this.filterForm.patchValue({ fromLocationName: params['fromLocationName'] });
      }

      if (params['toLocationName'] !== undefined) {
        this.filterForm.patchValue({ toLocationName: params['toLocationName'] });
      }

      if (
        params['fromEarliestPickupDate'] !== undefined &&
        params['toLatestDropoffDate'] !== undefined
      ) {
        this.filterForm.patchValue({
          dateOfJob: [
            moment(params['fromEarliestPickupDate'], 'YYYY-MM-DD').toDate(),
            moment(params['toLatestDropoffDate'], 'YYYY-MM-DD').toDate(),
          ],
        });
      }

      this.searchParameters = { ...this.createFilterVal() };
    });
  }

  handleSortChange(option: any) {
    this.formControls.sortBy.setValue(option.value.value);
    this.jobAnalyticsService.sendJobFilterApplyEvent('sortBy', option.value.value);
    void this.runSearch();
  }

  protected readonly TaskSupplierScheduleSource = TaskSupplierScheduleSource;

  showJobGrabbedConfirmation: boolean = false;
  showTravelScheduler: boolean = false;
  isScheduleRequired: boolean = false;

  onScheduleUpdated() {}

  handleScheduleNow() {
    this.isScheduleRequired = true;
    this.showTravelScheduler = true;
    this.showJobGrabbedConfirmation = false;
  }

  closeTravelScheduler() {
    this.showTravelScheduler = false;
    this.isScheduleRequired = false;
    this.supplierJobStoreService.setEditScheduleDialogVisible(false);
  }

  handleScheduleCompleted() {
    this.supplierJobStoreService.setAcceptJobDialogVisible(false);
    this.closeTravelScheduler();

    // if on job search page, remove the card once the schedule is added.
    if (this.inboxSubType == SupplierInboxSubType.JobSearch) {
      this.supplierJobStoreService.removeJob(this.selectedJob.job.id);
    } else {
      this.submitSearch();
    }
  }

  protected readonly SupplierInboxSubType = SupplierInboxSubType;
}
